import React, { FC, useEffect, useMemo, useRef, useState } from "react"
import { TrendCountry, TrendDemographic, TrendTrack } from "../../../../types"
import { Line, Pie } from "react-chartjs-2"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
  ArcElement
} from "chart.js"
import { lang } from "../../../../constants/languages"
import { KSpan } from "kahuna-base-react-components"
import { colorPaletteOptions } from "../../../../constants"
import { convertToDashedDate, getDailyTrendsChartTitle, getDailyTrendsEventText, withAlpha } from "../../../../utility"
import { ChartIndex, EnlargeIcon } from "../Trends"
import NotFound from "../../../NotFound"

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, BarElement, ArcElement)

type HoveredIndex = {
  datasetIndex: number
  dataIndex: number
  _key: string
}

const LineChartComponent = ({
  data,
  _key,
  hoveredIndex,
  setHoveredIndex,
  setEmpty
}: {
  data: any[]
  _key: string
  hoveredIndex: HoveredIndex
  setHoveredIndex: React.Dispatch<React.SetStateAction<HoveredIndex>>
  setEmpty?: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const changeColorOnHover = (index: number, color: string) => {
    const otherLineHovered =
      !(hoveredIndex.datasetIndex === index || hoveredIndex.datasetIndex === -1) && hoveredIndex._key === _key
    return otherLineHovered ? withAlpha(color, 0.4) : color
  }

  const handleHover = (_evt: unknown, elements: any[]) => {
    setHoveredIndex((prev: HoveredIndex) => {
      if (elements.length) {
        const { datasetIndex, index: dataIndex } = elements[0]

        if (datasetIndex === prev.datasetIndex && dataIndex === prev.dataIndex && _key === prev._key) {
          return prev
        }

        return { datasetIndex, dataIndex, _key }
      }
      if (prev.datasetIndex === -1 && prev.dataIndex === -1 && _key === prev._key) {
        return prev
      }
      return { datasetIndex: -1, dataIndex: -1, _key: "" }
    })
  }

  const baseChart = useMemo(() => {
    const grouped = {}
    const demographicStreamMap = {}
    const allDemographics: string[] = []
    data.forEach((item) => {
      const date = convertToDashedDate(item.date)
      const trendCountryList = item.trendCountryList || []

      trendCountryList.forEach((trendCountry: TrendCountry) => {
        const trendDemographicList = trendCountry.trendDemographicList || []

        trendDemographicList.forEach((trendDemographic: TrendDemographic) => {
          const keyValue = trendDemographic[_key]
          // Aggregate totals for main datasets
          if (!grouped[date]) grouped[date] = {}
          if (!grouped[date][keyValue]) grouped[date][keyValue] = { streams: 0 }
          grouped[date][keyValue].streams += trendDemographic.streams

          if (!demographicStreamMap[keyValue]) demographicStreamMap[keyValue] = 0
          demographicStreamMap[keyValue] += trendDemographic.streams

          if (!allDemographics.includes(keyValue)) allDemographics.push(keyValue)
        })
      })
    })

    const labels = Object.keys(grouped).sort()

    const datasets: any = []
    allDemographics.forEach((keyValue, index) => {
      const data: number[] = []
      labels.forEach((label) => {
        if (grouped[label][keyValue]?.streams) {
          data.push(grouped[label][keyValue].streams)
        } else {
          data.push(0)
        }
      })
      const randomColor = colorPaletteOptions[index]
      const isSinglePoint = labels?.length === 1
      datasets.push({
        label: keyValue,
        data,
        borderColor: randomColor,
        backgroundColor: randomColor,
        pointBorderColor: "transparent",
        tension: 0.4,
        pointRadius: isSinglePoint ? 5 : 0
      })
    })

    return {
      labels,
      datasets
    }
  }, [data])

  const chartData = useMemo(
    () => ({
      labels: baseChart.labels,
      datasets: baseChart.datasets.map((ds, i) => {
        const color = changeColorOnHover(i, ds.backgroundColor)
        return {
          ...ds,
          borderColor: color,
          backgroundColor: color
        }
      })
    }),
    [baseChart, hoveredIndex]
  )

  useEffect(() => {
    if (!setEmpty) return
    if (chartData?.labels?.length > 0) {
      setEmpty(false)
    }
  }, [chartData])

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    clip: false as const,
    interaction: {
      intersect: false,
      mode: "nearest" as const
    },
    plugins: {
      datalabels: {
        display: false
      },
      tooltip: {
        displayColors: false,
        backgroundColor: "#333",
        xAlign: "center" as const,
        yAlign: "bottom" as const,
        caretSize: 0,
        caretPadding: 10,
        padding: 10,
        bodyFont: {
          family: "Inter",
          weight: 400,
          size: 12,
          lineHeight: "16px"
        },
        titleFont: {
          family: "Inter",
          weight: 600,
          size: 14,
          lineHeight: "16px"
        },
        intersect: false
      },
      hover: {
        intersect: false
      },
      legend: {
        display: true,
        position: "bottom" as const,
        align: "end" as const,
        labels: {
          color: "#000",
          borderWidth: 0,
          font: {
            family: "Inter",
            weight: 500,
            size: 13,
            lineHeight: "13px"
          },
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 8,
          boxHeight: 8
        }
      }
    },
    scales: {
      y: {
        grid: {
          display: true,
          color: (ctx, index) => {
            return ctx.tick.value === ctx.scale.min ? "transparent" : "rgba(183, 183, 183, 0.30)"
          },
          drawTicks: true
        },

        border: {
          display: true,
          dash: [3, 3]
        },
        ticks: {
          color: "#999",
          font: {
            family: "Inter",
            weight: 500,
            size: 11,
            lineHeight: "12px"
          }
        }
      },
      x: {
        grid: {
          display: true,
          drawBorder: true,
          drawTicks: true,
          drawOnChartArea: false
        },
        border: {
          display: true
        },
        ticks: {
          color: "#999",
          font: {
            family: "Inter",
            weight: 500,
            size: 11,
            lineHeight: "12px"
          }
        }
      }
    },

    onHover: handleHover
  }

  return (
    <Line
      data={chartData}
      options={options}
      style={{
        width: "100%",
        height: "100%"
      }}
    />
  )
}

const PieChartComponent = ({
  data,
  _key,
  hideDatalabels,
  setEmpty,
  eventText
}: {
  data: any[]
  _key: string
  hideDatalabels?: boolean
  setEmpty?: React.Dispatch<React.SetStateAction<boolean>>
  eventText?: string
}) => {
  const [totalStream, setTotalStream] = useState<number>(0)
  const pieChartRef = useRef<ChartJS | null>(null)
  const totals = useMemo(() => {
    const demographicTotals = {}
    const demographicStreamMap = {}

    data.forEach((item) => {
      const trendCountryList = item.trendCountryList || []
      trendCountryList.forEach((trendCountry: TrendCountry) => {
        const trendDemographicList = trendCountry.trendDemographicList || []
        trendDemographicList.forEach((trendDemographic) => {
          const keyValue = trendDemographic[_key]
          if (!demographicStreamMap[keyValue]) demographicStreamMap[keyValue] = 0
          demographicStreamMap[keyValue] += trendDemographic.streams

          if (!demographicTotals[keyValue]) demographicTotals[keyValue] = 0
          demographicTotals[keyValue] += trendDemographic.streams
        })
      })
    })

    // ts-ignore
    const sortedTotals = Object.fromEntries(
      (Object.entries(demographicTotals) as [string, number][]).sort((a, b) => b[1] - a[1])
    )

    const labels = Object.keys(sortedTotals)
    const values = labels.map((label) => sortedTotals[label])

    const total = values.reduce((sum: number, value) => sum + Number(value), 0)
    setTotalStream(total)

    return {
      labels,
      datasets: [
        {
          label: `Streams by Demographic`,
          data: values,
          backgroundColor: labels.map((_, index) => colorPaletteOptions[index]),
          borderColor: "#fff",
          borderWidth: 1
        }
      ]
    }
  }, [data])

  useEffect(() => {
    if (!setEmpty) return
    if (totals?.labels?.length > 0) {
      setEmpty(false)
    }
  }, [totals])

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          font: {
            family: "Inter",
            weight: 500,
            size: 13,
            lineHeight: "13px"
          },
          color: "#000",
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 8,
          boxHeight: 8
        },
        onHover: (event, legendItem, legend) => {
          const chart = pieChartRef.current
          if (chart && chart.tooltip && chart.data && chart.data.datasets && chart.data.datasets[0]) {
            // Set the active element to the corresponding slice
            try {
              chart.setActiveElements([{ datasetIndex: 0, index: legendItem.index }])
              chart.tooltip?.setActiveElements([{ datasetIndex: 0, index: legendItem.index }], { x: 0, y: 0 }) // Optionally, set tooltip position
              chart.update()
            } catch (error) {
              // do nothing
            }
          }
        },
        onLeave: (event, legendItem, legend) => {
          const chart = pieChartRef.current
          if (chart && chart?.tooltip) {
            try {
              chart.setActiveElements([])
              chart.tooltip?.setActiveElements([], { x: 0, y: 0 })
              chart.update()
            } catch (error) {
              // do nothing
            }
          }
        }
      },
      tooltip: {
        displayColors: false,
        padding: 10,
        backgroundColor: "#FFF",
        titleColor: "#000",
        bodyColor: "#000",
        borderColor: "#E5E7EB",
        borderWidth: 1,
        bodyFont: {
          family: "Inter",
          weight: 400,
          color: "#000",
          size: 12,
          lineHeight: "16px"
        },
        titleFont: {
          family: "Inter",
          weight: 500,
          size: 13,
          lineHeight: "16px"
        },
        callbacks: {
          label: (context) => {
            const data = context.dataset.data as number[]
            const value = context.raw

            let percentage = 0

            if (totalStream <= 0) {
              percentage = 0
            } else {
              percentage = Number(((value / totalStream) * 100).toFixed(1))

              const isLast = context.dataIndex === data.length - 1

              if (isLast) {
                const prevSum = data.slice(0, -1).reduce((s, v) => s + Number(((v / totalStream) * 100).toFixed(1)), 0)
                percentage = Number((100 - prevSum).toFixed(1))
              }
            }

            return `${context.label}: ${value.toLocaleString()} ${eventText || lang.trends.streams} (%${percentage})`
          }
        }
      },
      datalabels: {
        display: (context) => {
          if (hideDatalabels) return false
          const active = context.chart.getActiveElements()
          if (active && active.length > 0 && context.dataIndex === active[0].index) {
            return true
          }
          return "auto"
        },
        color: "#fff",
        formatter: (value, context) => {
          const label = context.chart.data.labels[context.dataIndex]
          return label // or `${label}: ${value}` for more info
        },
        font: {
          family: "Inter",
          weight: 500,
          size: 14,
          lineHeight: "16px"
        }
      }
    }
  }

  //@ts-ignore
  return (
    <Pie
      ref={pieChartRef}
      data={totals}
      options={options}
      style={{
        width: "100%",
        height: "100%"
      }}
    />
  )
}

type StreamsDemographicProps = {
  streamsDemographic: TrendTrack[]
  fullScreenChartIndex: number | undefined
  setFullScreenChartIndex: (index: number | undefined) => void
  eventType: "dsp" | "social_consumption" | "social_production" | "download"
}

const StreamsDemographic: FC<StreamsDemographicProps> = (props) => {
  const { streamsDemographic, fullScreenChartIndex, setFullScreenChartIndex } = props

  const [hoveredIndex, setHoveredIndex] = useState<HoveredIndex>({
    datasetIndex: -1,
    dataIndex: -1,
    _key: ""
  })

  const [allChartsEmpty, setAllChartsEmpty] = useState<boolean>(true)
  const [filteredStreamsDaily, setFilteredStreamsDaily] = useState<TrendTrack[]>([])

  useEffect(() => {
    if (props.eventType === "social_consumption") {
      const filteredStreams = streamsDemographic.filter((stream) => stream.eventType === "social_consumption")
      setFilteredStreamsDaily(filteredStreams)
    } else if (props.eventType === "social_production") {
      const filteredStreams = streamsDemographic.filter((stream) => stream.eventType === "social_production")
      setFilteredStreamsDaily(filteredStreams)
    } else {
      setFilteredStreamsDaily(streamsDemographic)
    }
  }, [props.eventType, streamsDemographic])

  return (
    <div className="w-full flex-1 min-h-0 relative">
      {allChartsEmpty && (
        <div
          className="bg-[#FFF] absolute left-0 top-0 w-full h-full flex items-center justify-center rounded-[10px]"
          style={{
            borderRadius: "10px",
            border: "1px solid var(--stroke-soft-200, #F3F3F3)",
            background: "var(--bg-white-0, #FFF)",
            boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
          }}
        >
          <div className="flex justify-center">
            <NotFound title={lang.not_found.trends.title} description={lang.not_found.trends.desc} imageWidth={400} />
          </div>
        </div>
      )}
      {fullScreenChartIndex && (
        <div
          className="bg-[#FFF] absolute left-0 top-0 w-full h-full flex items-center justify-center rounded-[10px]"
          style={{
            borderRadius: "10px",
            border: "1px solid var(--stroke-soft-200, #F3F3F3)",
            background: "var(--bg-white-0, #FFF)",
            boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
          }}
        >
          <div className="w-full h-full py-5 px-7 flex justify-center items-center relative flex-col gap-4">
            <div
              onClick={() => {
                setFullScreenChartIndex(undefined)
              }}
              className="flex items-center cursor-pointer justify-center p-1 rounded-[4px] hover:bg-[#F7F7F7] absolute right-2 top-2"
            >
              <img src={"/close.svg"} className="w-4 h-4" />
            </div>
            <KSpan
              text={getDailyTrendsChartTitle(fullScreenChartIndex, props.eventType)}
              fontSize={16}
              color="#000"
              fontWeight={500}
            />
            <div
              className="w-full flex-1 min-h-0 flex items-center justify-center"
              onMouseLeave={() => {
                if (
                  fullScreenChartIndex === ChartIndex.DEMOGRAPHIC_LINE_CHART_AGE ||
                  fullScreenChartIndex === ChartIndex.DEMOGRAPHIC_LINE_CHART_SEX
                ) {
                  setHoveredIndex((prev) => {
                    return { ...prev, datasetIndex: -1, dataIndex: -1, _key: "" }
                  })
                }
              }}
            >
              {fullScreenChartIndex === ChartIndex.DEMOGRAPHIC_PIE_CHART_AGE && (
                <PieChartComponent
                  data={filteredStreamsDaily}
                  _key={"ageGroup"}
                  eventText={getDailyTrendsEventText(props.eventType)}
                />
              )}
              {fullScreenChartIndex === ChartIndex.DEMOGRAPHIC_PIE_CHART_SEX && (
                <PieChartComponent
                  data={filteredStreamsDaily}
                  _key={"sex"}
                  eventText={getDailyTrendsEventText(props.eventType)}
                />
              )}
              {fullScreenChartIndex === ChartIndex.DEMOGRAPHIC_LINE_CHART_AGE && (
                <LineChartComponent
                  data={filteredStreamsDaily}
                  _key={"ageGroup"}
                  setHoveredIndex={setHoveredIndex}
                  hoveredIndex={hoveredIndex}
                />
              )}
              {fullScreenChartIndex === ChartIndex.DEMOGRAPHIC_LINE_CHART_SEX && (
                <LineChartComponent
                  data={filteredStreamsDaily}
                  _key={"sex"}
                  setHoveredIndex={setHoveredIndex}
                  hoveredIndex={hoveredIndex}
                />
              )}
            </div>
          </div>
        </div>
      )}
      <div
        className="w-full h-full flex flex-col gap-3"
        style={{
          opacity: fullScreenChartIndex || allChartsEmpty ? 0 : 1,
          pointerEvents: fullScreenChartIndex || allChartsEmpty ? "none" : "auto"
        }}
      >
        <div className="flex flex-row gap-3 w-full flex-1 min-h-0">
          <div
            className="py-5 px-7 h-full aspect-square flex flex-col gap-2 relative"
            style={{
              borderRadius: "10px",
              border: "1px solid var(--stroke-soft-200, #F3F3F3)",
              background: "var(--bg-white-0, #FFF)",
              boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
            }}
          >
            <EnlargeIcon setIndex={setFullScreenChartIndex} chartIndex={ChartIndex.DEMOGRAPHIC_PIE_CHART_AGE} />
            <div className="flex justify-center">
              <KSpan
                text={getDailyTrendsChartTitle(ChartIndex.DEMOGRAPHIC_PIE_CHART_AGE, props.eventType)}
                fontSize={16}
                color="#000"
                fontWeight={500}
              />
            </div>
            <div className="flex-1 min-h-0 aspect-square">
              <PieChartComponent
                data={filteredStreamsDaily}
                _key={"ageGroup"}
                hideDatalabels={true}
                setEmpty={setAllChartsEmpty}
              />
            </div>
          </div>
          <div
            onMouseLeave={() => {
              setHoveredIndex((prev) => {
                return { ...prev, datasetIndex: -1, dataIndex: -1, _key: "" }
              })
            }}
            className="py-5 px-7 flex-1 flex flex-col gap-2 relative"
            style={{
              borderRadius: "10px",
              border: "1px solid var(--stroke-soft-200, #F3F3F3)",
              background: "var(--bg-white-0, #FFF)",
              boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
            }}
          >
            <EnlargeIcon setIndex={setFullScreenChartIndex} chartIndex={ChartIndex.DEMOGRAPHIC_LINE_CHART_AGE} />

            <div className="mb-4 flex justify-center">
              <KSpan
                text={getDailyTrendsChartTitle(ChartIndex.DEMOGRAPHIC_LINE_CHART_AGE, props.eventType)}
                fontSize={16}
                color="#000"
                fontWeight={500}
              />
            </div>
            <div className="w-full flex-1 min-h-0">
              <LineChartComponent
                data={filteredStreamsDaily}
                _key={"ageGroup"}
                setHoveredIndex={setHoveredIndex}
                hoveredIndex={hoveredIndex}
                setEmpty={setAllChartsEmpty}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-row gap-3 w-full flex-1 min-h-0">
          <div
            onMouseLeave={() => {
              setHoveredIndex((prev) => {
                return { ...prev, datasetIndex: -1, dataIndex: -1, _key: "" }
              })
            }}
            className="py-5 px-7 flex-1 flex flex-col gap-2 relative"
            style={{
              borderRadius: "10px",
              border: "1px solid var(--stroke-soft-200, #F3F3F3)",
              background: "var(--bg-white-0, #FFF)",
              boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
            }}
          >
            <EnlargeIcon setIndex={setFullScreenChartIndex} chartIndex={ChartIndex.DEMOGRAPHIC_LINE_CHART_SEX} />
            <div className="flex justify-center">
              <KSpan
                text={getDailyTrendsChartTitle(ChartIndex.DEMOGRAPHIC_LINE_CHART_SEX, props.eventType)}
                fontSize={16}
                color="#000"
                fontWeight={500}
              />
            </div>
            <div className="w-full flex-1 min-h-0">
              <LineChartComponent
                data={filteredStreamsDaily}
                _key={"sex"}
                setHoveredIndex={setHoveredIndex}
                hoveredIndex={hoveredIndex}
                setEmpty={setAllChartsEmpty}
              />
            </div>
          </div>
          <div
            className="py-5 px-7 h-full aspect-square flex flex-col gap-2 relative"
            style={{
              borderRadius: "10px",
              border: "1px solid var(--stroke-soft-200, #F3F3F3)",
              background: "var(--bg-white-0, #FFF)",
              boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
            }}
          >
            <EnlargeIcon setIndex={setFullScreenChartIndex} chartIndex={ChartIndex.DEMOGRAPHIC_PIE_CHART_SEX} />

            <div className="flex justify-center">
              <KSpan
                text={getDailyTrendsChartTitle(ChartIndex.DEMOGRAPHIC_PIE_CHART_SEX, props.eventType)}
                fontSize={16}
                color="#000"
                fontWeight={500}
              />
            </div>
            <div className="flex justify-center w-full">
              <PieChartComponent
                data={filteredStreamsDaily}
                _key={"sex"}
                hideDatalabels={true}
                setEmpty={setAllChartsEmpty}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default StreamsDemographic
